import React, { useContext, useState } from "react"
import moment from "moment"

import { funEU } from "../../utils/firebase"
import { AlertsContext } from "../../utils/providers/alerts"
import { AuthContext } from "../../utils/providers/auth"
import { formatPointsValue } from "../../utils/stableford"
import exportRedemptions from "./_utils/export-redemptions"
import "./points-redemptions.scss"

// UI components
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import WindowCard from "../../components/ui/window-card/window-card"
import Button from "../../components/ui/button/button"
import Datepicker from "../../components/ui/datepicker/datepicker"
import Select from "../../components/ui/select/select"
import Table from "../../components/structure/table/table"
import Badge from "../../components/ui/badge/badge"

const fetchRedemptionReport = funEU.httpsCallable("partnerRedemptionReport")

/** Sentinel for the "every login" option in the login filter. */
const ALL_LOGINS = "ALL"

/** The window the page opens on, in days back from today. */
const DEFAULT_DAYS = 30

const formatDateTime = (millis) => (millis ? moment(millis).format("DD/MM/YYYY HH:mm") : "-")
const formatDay = (millis) => (millis ? moment(millis).format("DD/MM/YYYY") : "-")

const sumPoints = (rows) => rows.reduce((total, row) => total + Number(row.points || 0), 0)
const formatPoints = (points) => Number(points || 0).toLocaleString("en-GB")

/**
 * Stableford redemption report.
 *
 * The reconciliation surface for a partner's finance team: what they spent in a
 * date range, what was reversed in it, and the net movement between the two,
 * exportable as a spreadsheet to sit alongside their own cash report.
 *
 * It is deliberately its own page rather than a card on the redemption tool -
 * the people pulling it are not the people redeeming, and they want to pick
 * dates and export rather than scroll past a live form to do it.
 */
export default function PointsRedemptions() {
	// Dates are held as milliseconds, the value the Datepicker round-trips
	const [startDate, setStartDate] = useState(moment().subtract(DEFAULT_DAYS - 1, "days").startOf("day").valueOf())
	const [endDate, setEndDate] = useState(moment().startOf("day").valueOf())

	const [loading, setLoading] = useState(false)
	const [report, setReport] = useState(null)
	const [login, setLogin] = useState(ALL_LOGINS)

	const { pushAlert } = useContext(AlertsContext)
	const { user } = useContext(AuthContext)

	const partnerName = String(user?.points_partner?.name || "").trim()

	// Every login that appears in the loaded report, for the filter. Only one
	// account redeems for most partners, so the filter earns its place on the
	// page only once a second login shows up in the data.
	const loginOptions = { [ALL_LOGINS]: "All logins" }
	if (report) {
		[...report.redemptions, ...report.reversals].forEach((row) => {
			if (row.partnerUID) loginOptions[row.partnerUID] = row.redeemedBy || row.partnerUID
		})
	}
	const hasSeveralLogins = Object.keys(loginOptions).length > 2

	const inFilter = (row) => login === ALL_LOGINS || row.partnerUID === login
	const redemptions = report ? report.redemptions.filter(inFilter) : []
	const reversals = report ? report.reversals.filter(inFilter) : []

	// Redemptions count in full against the window they were made in, whether or
	// not they have been voided since - they were live on the partner's cash
	// report for it. Reversals are then the movement out, on the day they were
	// voided, which is what keeps the two systems reconcilable month to month.
	const redeemedPoints = sumPoints(redemptions)
	const reversedPoints = sumPoints(reversals)
	const netPoints = redeemedPoints - reversedPoints
	const voidedSince = redemptions.filter((row) => row.voided)

	const applyPreset = (start, end) => {
		setStartDate(start.startOf("day").valueOf())
		setEndDate(end.startOf("day").valueOf())
		setReport(null)
	}

	const loadReport = async () => {
		setLoading(true)
		setReport(null)
		setLogin(ALL_LOGINS)

		try {
			const { data } = await fetchRedemptionReport({
				// The end date is inclusive on screen, so the range handed to the
				// server runs to the start of the following day
				start: moment(startDate).startOf("day").valueOf(),
				end: moment(endDate).add(1, "day").startOf("day").valueOf(),
			})

			setReport({
				start: startDate,
				end: endDate,
				generated: data?.generated || Date.now(),
				redemptions: data?.redemptions || [],
				reversals: data?.reversals || [],
			})
		} catch (error) {
			pushAlert({
				type: "ERROR",
				title: "Report failed",
				body: error?.message || "Unable to load the redemption report.",
			})
		} finally {
			setLoading(false)
		}
	}

	const rangeLabel = report ? `${formatDay(report.start)} to ${formatDay(report.end)}` : ""
	const canLoad = Boolean(startDate) && Boolean(endDate) && endDate >= startDate

	return (
		<Tile>
			<Title>
				<h1>Redemption report</h1>
				<p>
					Stableford points redeemed through {partnerName ? `your ${partnerName} logins` : "your logins"}, by redemption date, alongside anything reversed over the same period. Every 10 points is worth £1.00.
				</p>
			</Title>

			<div className="points-redemptions">
				<WindowCard
					title="Choose a period"
					subtitle="Both dates are included in the report. Defaults to the last 30 days.">
					<div className="points-redemptions__filters">
						<Datepicker
							label="From"
							placeholder="From"
							showLabel={true}
							value={startDate}
							onSelect={(date) => {
								setStartDate(date)
								setReport(null)
							}}
						/>

						<Datepicker
							label="To"
							placeholder="To"
							showLabel={true}
							value={endDate}
							onSelect={(date) => {
								setEndDate(date)
								setReport(null)
							}}
						/>

						<Button
							label="Load report"
							loading={loading}
							loadingText="Loading..."
							disabled={!canLoad}
							onClick={() => loadReport()}
						/>

						<Button
							type="secondary"
							label="Export to XLSX"
							disabled={!report}
							onClick={() => exportRedemptions({ redemptions, reversals, start: report.start, end: report.end })}
						/>
					</div>

					<div className="points-redemptions__presets">
						<Button
							filters={true}
							small={true}
							label="Last 30 days"
							onClick={() => applyPreset(moment().subtract(DEFAULT_DAYS - 1, "days"), moment())}
						/>
						<Button
							filters={true}
							small={true}
							label="This month"
							onClick={() => applyPreset(moment().startOf("month"), moment())}
						/>
						<Button
							filters={true}
							small={true}
							label="Last month"
							onClick={() => applyPreset(moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month"))}
						/>
						<Button
							filters={true}
							small={true}
							label="This year"
							onClick={() => applyPreset(moment().startOf("year"), moment())}
						/>
					</div>

					{!canLoad && <p className="points-redemptions__warning">The end of the period has to fall on or after the start.</p>}
				</WindowCard>

				{report && (
					<>
						<WindowCard
							className="has-top-margin"
							title={`Summary for ${rangeLabel}`}
							subtitle="Redemptions count against the period they were made in; reversals against the period they were voided in.">
							{hasSeveralLogins && (
								<div className="points-redemptions__login">
									<Select
										label="Redeemed by"
										placeholder="Redeemed by"
										showLabel={true}
										value={loginOptions[login]}
										options={loginOptions}
										onSelect={(option) => setLogin(option.option)}
									/>
								</div>
							)}

							<div className="points-redemptions__stats">
								<div className="points-redemptions__stat is-primary">
									<p className="points-redemptions__stat-label">Redeemed in period</p>
									<p className="points-redemptions__stat-value">{formatPointsValue(redeemedPoints)}</p>
									<p className="points-redemptions__stat-meta">
										{formatPoints(redeemedPoints)} points across {redemptions.length.toLocaleString("en-GB")} redemptions
									</p>
								</div>

								<div className="points-redemptions__stat">
									<p className="points-redemptions__stat-label">Reversed in period</p>
									<p className="points-redemptions__stat-value">{formatPointsValue(reversedPoints)}</p>
									<p className="points-redemptions__stat-meta">
										{formatPoints(reversedPoints)} points across {reversals.length.toLocaleString("en-GB")} reversals
									</p>
								</div>

								<div className="points-redemptions__stat is-primary">
									<p className="points-redemptions__stat-label">Net movement</p>
									<p className="points-redemptions__stat-value">{formatPointsValue(netPoints)}</p>
									<p className="points-redemptions__stat-meta">{formatPoints(netPoints)} points</p>
								</div>
							</div>

							{voidedSince.length > 0 && (
								<p className="points-redemptions__note">
									{voidedSince.length} of the redemptions below {voidedSince.length === 1 ? "has" : "have"} been voided since, returning {formatPoints(sumPoints(voidedSince))} points to the client. They are still counted above because they were live at the time. Whichever period they were voided in carries the reversal.
								</p>
							)}

							<p className="points-redemptions__note">Statuses are shown as they stood at {formatDateTime(report.generated)}.</p>
						</WindowCard>

						<WindowCard
							className="has-top-margin"
							title="Redemptions"
							subtitle={`Points spent between ${rangeLabel}.`}>
							<div className="points-redemptions__table">
								<Table
									headings={["Redemption date", "Booking reference", "Client", "Email", "Redeemed by", "Points", "Value", "Status"]}
									noResults={redemptions.length === 0}
									noResultsMessage="No Stableford points were redeemed over this period.">
									{redemptions.map((redemption) => (
										<tr key={redemption.id}>
											<td>{formatDateTime(redemption.created)}</td>
											<td>{redemption.reference}</td>
											<td>{redemption.clientName || "-"}</td>
											<td>{redemption.clientEmail || "-"}</td>
											<td>{redemption.redeemedBy || "-"}</td>
											<td>{formatPoints(redemption.points)}</td>
											<td>{formatPointsValue(redemption.points)}</td>
											<td>
												<Badge
													type={redemption.voided ? "NEGATIVE" : "POSITIVE"}
													label={redemption.voided ? "Voided" : "Applied"}
												/>
											</td>
										</tr>
									))}
								</Table>
							</div>
						</WindowCard>

						<WindowCard
							className="has-top-margin"
							title="Reversals"
							subtitle="Redemptions voided over this period, whenever they were originally made.">
							<div className="points-redemptions__table">
								<Table
									headings={["Voided", "Originally redeemed", "Booking reference", "Client", "Redeemed by", "Points", "Value", "Reason"]}
									noResults={reversals.length === 0}
									noResultsMessage="No redemptions were voided over this period.">
									{reversals.map((reversal) => (
										<tr key={reversal.id}>
											<td>{formatDateTime(reversal.voidedAt)}</td>
											<td>{formatDateTime(reversal.created)}</td>
											<td>{reversal.reference}</td>
											<td>{reversal.clientName || "-"}</td>
											<td>{reversal.redeemedBy || "-"}</td>
											<td>{formatPoints(reversal.points)}</td>
											<td>{formatPointsValue(reversal.points)}</td>
											<td>{reversal.voidReason || "-"}</td>
										</tr>
									))}
								</Table>
							</div>
						</WindowCard>
					</>
				)}
			</div>
		</Tile>
	)
}
